// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, IntlContext, IntlShape } from "react-intl";

import { IEnergyMeter, ITariffCircuit } from "shared/types/parameter";
import { ICloudParameter } from "shared/types/cloudtypes";
import { isSet, roundToDigits } from "shared/utils/utils";
import { IState } from "states/states";
import { IAction } from "actions";
import {
  formatPowerValue,
  formatEnergyValue,
  getVirtualSiteSourceName,
  renderVirtualSiteIcon,
} from "guiutils";

import ProgressBar from "./ProgressBar";
import { IChartTariffNode } from "./ChartBase";

const DIGITS_TO_SHOW = 2;

interface ITariffCircuitExtProps {
  tarifflineInfo: IChartTariffNode;
  showHierarchy: boolean;
  showCategory: boolean;
  trendMember?: "energy" | "power" | "costs";
  filterText?: string;
  selectedCategory: string | null | undefined;
  activeRootId: number | undefined;
  dropDownHierarchyMode?: boolean;
  isDashboard?: boolean;
  // [optional] callback when the selected items have changed
  //    parameter selectedItems provides a list of the currently selected items
  onTariffClicked?: (event: React.MouseEvent, clickedTariffId: number) => void;
  onCategoryClicked?: (event: React.MouseEvent, inCategoryies: string | undefined) => void;
}

interface ITariffCircuitProps {
  tariffCircuits: ITariffCircuit[];
  energymeters: IEnergyMeter[];
  parameter: ITariffCircuit | undefined;
  trendMember: "energy" | "power" | "costs";
  currency: string;
  showId: boolean;
  virtualSiteName: string | undefined;
}

const mapStateToProps = (state: IState, ownProps: ITariffCircuitExtProps) => {
  const tariffCircuits = state.para.parameter?.tariffline ?? [];
  const tcIndex = tariffCircuits.findIndex(
    (curItem, curIndex) => (curItem.id ?? curIndex) === ownProps.tarifflineInfo.id,
  );
  const parameter = tariffCircuits[tcIndex];
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: ITariffCircuitProps = {
    tariffCircuits,
    energymeters: state.para.parameter?.energymeter ?? [],
    parameter,
    trendMember: isSet(ownProps.trendMember) ? ownProps.trendMember : state.gui.trendMember,
    currency: "EUR",
    showId: Boolean(curUser?.accessRights.setVirtualParameters),
    virtualSiteName:
      ownProps.isDashboard ? undefined : (
        getVirtualSiteSourceName(
          (state.para.parameter as ICloudParameter | undefined)?.virtualSite?.tariffline,
          tcIndex,
          state.cached.selectedSite,
          state.site.siteList,
        )
      ),
  };
  return props;
};

interface ITariffCircuitDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITariffCircuitDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  relatedTo: {
    id: "related to: ",
  },
  energyMeters: {
    id: "Energy Meters",
  },
  targetValue: {
    id: "Target Value",
  },
  noDataAvailable: {
    id: "No data available.",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  virtualSite: { site: string };
}>({
  virtualSite: {
    id: "This item's data originates from {site}",
  },
});

class TariffCircuitComponent extends React.Component<
  ITariffCircuitProps & ITariffCircuitExtProps & ITariffCircuitDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  // enable/disable gray-out of items based on hierarchy selection
  private readonly isHierarchyGrayOutEnabled = false;

  public render() {
    const parent =
      this.props.showHierarchy ?
        this.props.tariffCircuits.find((curItem) => curItem.id === this.props.parameter?.parent)
      : undefined;
    const emContributions = this.props.parameter?.contributions?.map((curContr) => curContr.em);
    const energyMeters = this.props.energymeters
      .filter((curItem) => isSet(curItem.id) && emContributions?.includes(curItem.id))
      .map((curItem) => curItem.name);
    const tooltipMeters =
      energyMeters ?
        `${this.context.formatMessage(messages.energyMeters)}: ${energyMeters.join(", ")}`
      : undefined;
    const tariffCircuit = this.props.tariffCircuits.find(
      (curItem, curIndex) => (curItem.id ?? curIndex) === this.props.tarifflineInfo.id,
    );

    const valueText =
      this.props.trendMember === "costs" ?
        `${
          isSet(this.props.tarifflineInfo.value) ?
            roundToDigits(this.props.tarifflineInfo.value, DIGITS_TO_SHOW)
          : "-"
        } ${this.props.currency}`
      : this.props.trendMember === "energy" ?
        formatEnergyValue(this.props.tarifflineInfo.value, tariffCircuit?.unit)
      : formatPowerValue(this.props.tarifflineInfo.value, tariffCircuit?.unit);

    const maxValueText =
      this.props.trendMember === "costs" ?
        `${roundToDigits(this.props.tarifflineInfo.maxValue, DIGITS_TO_SHOW)} ${
          this.props.currency
        }`
      : this.props.trendMember === "energy" ?
        formatEnergyValue(this.props.tarifflineInfo.maxValue, tariffCircuit?.unit)
      : formatPowerValue(this.props.tarifflineInfo.maxValue, tariffCircuit?.unit);

    const percentage = Math.abs(
      ((this.props.tarifflineInfo.value ?? 0) * 100) / (this.props.tarifflineInfo.maxValue ?? 1),
    );

    const tooltipPower =
      isSet(this.props.tarifflineInfo.value) && !isNaN(this.props.tarifflineInfo.value) ?
        `${this.context.formatMessage(messages.relatedTo)}${maxValueText}\n${
          this.props.tarifflineInfo.isRelatedToTargetValue ?
            `${this.context.formatMessage(messages.targetValue)} [${
              this.props.tarifflineInfo.relatedDateString ?? ""
            }]`
          : `${this.props.tarifflineInfo.relatedDateString ?? ""}`
        }`
      : `${this.context.formatMessage(messages.noDataAvailable)}`;

    const tooltip = `${tooltipPower}\n\n${tooltipMeters}`;

    const value = valueText.split(" ")[0];
    const unit = valueText.split(" ").length > 1 ? valueText.split(" ")[1] : "-";

    const isHighlighted =
      !isSet(this.props.filterText) ||
      this.props.filterText === "" ||
      this.props.tarifflineInfo.name
        .toLocaleLowerCase()
        .includes(this.props.filterText.toLocaleLowerCase());

    const canClickShowChildren =
      this.isHierarchyGrayOutEnabled &&
      this.props.dropDownHierarchyMode === true &&
      this.props.tarifflineInfo.hasChildren === true &&
      this.props.activeRootId !== this.props.tarifflineInfo.id;

    const canClickShowParent =
      this.isHierarchyGrayOutEnabled &&
      this.props.dropDownHierarchyMode === true &&
      isSet(this.props.tarifflineInfo.parentId) &&
      this.props.activeRootId === this.props.tarifflineInfo.id;

    return (
      <tr
        className={`tariffcircuititem ${isHighlighted ? "" : "grayout"} ${
          this.props.dropDownHierarchyMode === false && this.isHierarchyGrayOutEnabled ?
            "showMousePointer"
          : ""
        } ${canClickShowParent ? " bold" : ""}`}
        onClick={(event) => {
          if (this.props.dropDownHierarchyMode === false) {
            this.props.onTariffClicked?.(event, this.props.tarifflineInfo.id);
          }
        }}
      >
        {this.props.showId && <td className="optional-lg">{this.props.parameter?.id}</td>}
        <td
          className={`tcname ${canClickShowChildren ? "link" : ""}`}
          onClick={(event) => {
            if (canClickShowChildren) {
              event.stopPropagation();
              event.preventDefault();
              this.props.onTariffClicked?.(event, this.props.tarifflineInfo.id);
            }
          }}
        >
          <div
            style={{
              background: this.props.tarifflineInfo.color,
            }}
          />
          <span
            className={`${
              this.props.tarifflineInfo.isDisabled && this.isHierarchyGrayOutEnabled ?
                "grayout"
              : ""
            }`}
          >
            {this.props.parameter?.name}
          </span>
          {this.props.virtualSiteName &&
            renderVirtualSiteIcon(
              this.context.formatMessage(valueMessages.virtualSite, {
                site: this.props.virtualSiteName,
              }),
            )}
        </td>
        {this.props.showHierarchy && (
          <td
            className={`optional-lg ${canClickShowParent ? "link" : ""} ${
              this.props.tarifflineInfo.isDisabled && this.isHierarchyGrayOutEnabled ?
                "grayout"
              : ""
            }`}
            onClick={(event) => {
              if (canClickShowParent) {
                event.stopPropagation();
                event.preventDefault();
                this.props.onTariffClicked?.(event, parent?.id ?? -1);
              }
            }}
          >
            {parent?.name}
          </td>
        )}
        {this.props.showCategory && (
          <td
            className={`optional-lg ${
              (
                (this.props.parameter?.categories?.length ?? 0) > 0 &&
                this.props.parameter?.categories?.[0] !== ""
              ) ?
                "link"
              : ""
            } ${
              this.props.tarifflineInfo.isDisabled && this.isHierarchyGrayOutEnabled ?
                "grayout"
              : ""
            }`}
            onClick={(event) => {
              if (
                isSet(this.props.selectedCategory) &&
                this.props.parameter?.categories?.[0] === this.props.selectedCategory
              ) {
                this.props.onCategoryClicked?.(event, undefined);
              } else {
                isSet(this.props.parameter?.categories?.[0]) &&
                  this.props.onCategoryClicked?.(event, this.props.parameter.categories?.[0]);
              }
            }}
          >
            {this.props.parameter?.categories?.[0]}
          </td>
        )}
        <td
          className={`value-col ${
            this.props.tarifflineInfo.isDisabled && this.isHierarchyGrayOutEnabled ? "grayout" : ""
          }`}
        >
          {isSet(this.props.tarifflineInfo.value) && !isNaN(this.props.tarifflineInfo.value) ?
            <ProgressBar
              value={value}
              unit={unit}
              percentage={percentage}
              tooltip={tooltip}
              disabled={this.props.tarifflineInfo.isDisabled && this.isHierarchyGrayOutEnabled}
            ></ProgressBar>
          : <></>}
        </td>
      </tr>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TariffCircuitComponent);
